<template>
  <div>
    <div class="text-center">
      <el-image v-if="promotion.banner_media" :src="imgProxy(promotion.banner_media?.url)"
        :alt="dt(promotion?.title_translations)">
        <template #error>
          <img src="/static/assets/front-end/img/image-place-holder.png" :alt="dt(promotion?.title_translations)"
            style="width: 100%;">
        </template>
      </el-image>
    </div>

    <div class="bg-white text-dark rounded shadow-lg p-3 p-md-4 mt-4 d-flex justify-content-between align-items-center">
      <div>{{ t("promotions.code") }}：<span>{{ promotion.codes[0] }}</span></div>
      <div>
        <button class="btn btn-outline-primary" @click="handleCreateDiscountCode">{{ t("promotions.saveCode") }}</button>
      </div>
    </div>

    <div v-if="description_translations" class="bg-white text-dark rounded shadow-lg p-3 p-md-4 mt-4"
      style="font-size: 22px;font-weight:700;">
      <div v-html="description_translations"></div>
    </div>

    <!-- 指定商品 -->
    <template v-if="promotion.condition_scope == 'products'">
      <div class="row mt-3">
        <div class="col-lg-3 col-md-4 col-sm-4 col-6 mb-2 p-2" v-for="product in promotion.whitelisted_store_products"
          :key="product.id">
          <ProductItem :product="product" />
        </div>
      </div>
    </template>

    <!-- 指定活動 -->
    <template v-else-if="promotion.condition_scope == 'promotions'">
      <div class="row mt-3">
        <div class="col-lg-3 col-md-4 col-sm-4 col-6 mb-2 p-2" v-for="prom in promotion.whitelisted_store_promotions"
          :key="promotion.id">
          <PromotionItem :promotion="prom" />
        </div>
      </div>
    </template>

    <!-- 指定活動組 -->
    <template v-else-if="promotion.condition_scope == 'promotion_groups'">
      <div class="row mt-3">
        <div class="col-lg-3 col-md-4 col-sm-4 col-6 mb-2 p-2"
          v-for="prom in promotion.whitelisted_store_promotion_groups" :key="promotion.id">
          <PromotionItem :promotion="prom" />
        </div>
      </div>
    </template>

    <!-- 指定分類 or 全站 -->
    <template v-else>
      <div class="row mt-3">
        <div class="col-lg-3 col-md-4 col-sm-4 col-6 mb-2 p-2" v-for="product in products.data" :key="product.id">
          <ProductItem :product="product" />
        </div>
      </div>
      <div class="col-12 text-center p-2">
        <v3-infinite-loading @infinite="loadData">
          <template #complete>
            <h4 class="pt-2">{{ t("products.empty") }}</h4>
          </template>
          <template #spinner>
            <span>loading...</span>
          </template>
        </v3-infinite-loading>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, reactive } from 'vue'
import { useI18n } from "vue-i18n"
import V3InfiniteLoading from "v3-infinite-loading";
import {
  dataTranslations as dt,
  parseVideoTag,
  imgProxy,
} from "../utils"
import { notificationErrors } from "../utils"
import { _api_getPromotionProducts, _api_postAccountPromotionCodes } from "../api/index.js";

import ProductItem from "./products/ProductItem.vue"
import PromotionItem from "./promotions/PromotionItem.vue"

const { t, n, locale } = useI18n()

const props = defineProps({
  promotion: Object,
})

const description_translations = computed(() => {
  if (props.promotion?.description_translations && !!Object.keys(props.promotion.description_translations).length) {
    let desc = dt(props.promotion.description_translations)
    return parseVideoTag(desc)
  }
  return null
})

const filterParams = reactive({
  page: props.page || 1,
  limit: props.limit || 12,
  fields: 'price_range',
})
const products = reactive({
  data: [],
  paging: null,
})

const trackingViewContent = (productList) => {
  if (productList?.length && props.promotion?.id) {
    const aibTrackingItems = [];
    for (const _product of productList) {
      aibTrackingItems.push({
        "event_id": props.promotion.id,
        "event_name": dt(props.promotion.title_translations),
        "store_product_id": _product.id,
        "prod_name": _product.name,
        "price": _product.price_range?.min_sale_price || 1,
        "quantity": 1,
      });
    };
    const dataPromotion = { ...props.promotion, aib_tracking_items: aibTrackingItems };
    const eventID = aibTrackers.generateCustomEventId();
    aibTrackers.viewContentPromotion(dataPromotion, eventID);
    gtmTrackers.viewContentPromotion(dataPromotion, eventID);
    fbqTrackers.viewContentPromotion(dataPromotion, eventID);
    ttqTrackers.viewContentPromotion(dataPromotion, eventID);
    gtagTrackers.viewContentPromotion(dataPromotion, eventID);
  }
}

const loadData = async ($state) => {
  // window.fullPageLoading("show")
  try {
    const resp = await _api_getPromotionProducts(props.promotion.id, filterParams)
    products.data = [...products.data, ...resp.data]
    products.paging = resp.paging

    filterParams.page++
    if (resp.data.length < filterParams.limit) {
      $state.complete()
    } else {
      $state.loaded()
    }
    trackingViewContent(resp.data);
  } catch (err) {
    $state.error()
    notificationErrors(err)
  } finally {
    // window.fullPageLoading("hide")
  }
}

const handleCreateDiscountCode = async () => {
  window.fullPageLoading("show")
  try {
    const params = {
      code: props.promotion.codes[0],
      action: "get_or_create",
    }

    const resp = await _api_postAccountPromotionCodes(params)
    // console.log("handleDiscountCode: ", resp.data)
    if (resp.data) {
      swal.fire({
        title: '成功儲存折扣碼',
        type: 'success',
        showCancelButton: true,
        confirmButtonColor: 'var(--secondary_color)',
        confirmButtonText: t("carts.continueShopping2"),
        cancelButtonColor: 'var(--primary_color)',
        cancelButtonText: '我的折扣碼',
      }).then((result) => {
        // console.log('result', result);
        if (result.value) {
          // console.log('繼續購物');
        } else if (result.dismiss == "cancel") {
          // console.log('我的折扣碼');
          window.location.href = "/account/promotion-codes";
        }
      })
    }
  } catch (err) {
    notificationErrors(err.response.data)
  } finally {
    window.fullPageLoading("hide")
  }
}
</script>